import Vue from 'vue';

import { FileTaskTypes, TaskNotificationReason } from '../constants/file';
import { Task, TaskData, TaskListener, TaskManager } from './types';

export class FileTasksService {
  private _id = 0;
  private _listeners: TaskListener[] = [];

  // Wrap the tasks array in an object so it will be observable
  // (Vue.observable() only accepts objects)
  private _observable: { tasks: Task<TaskData>[] } = Vue.observable({ tasks: [] });

  public addTask<Data extends TaskData>(
    files: string[],
    type: FileTaskTypes,
    data: Data
  ): TaskManager<Data> {
    const task: Task<Data> = {
      id: this._id++,
      files: [...files],
      type,
      data: Vue.observable(data), // Use observable so components can react to this data
      update: (updateCb: (data: Data) => void): void => {
        updateCb(task.data);
        this.notifyTaskListeners(task, TaskNotificationReason.UPDATED);
      },
    };

    this.notifyTaskListeners(task, TaskNotificationReason.ADDED);

    this._observable.tasks.push(task);

    return {
      data: task.data,
      removeTask: () => {
        const taskIdx = this._observable.tasks.findIndex(t => t.id === task.id);
        if (taskIdx !== -1) {
          this._observable.tasks.splice(taskIdx, 1);
        }

        // Notify the tasks' listeners of its final state
        this.notifyTaskListeners(task, TaskNotificationReason.REMOVED);
      },
      update: task.update,
    };
  }

  /**
   * @param task - the task whose files' listeners to notify
   * @param reason - the reason for the notification
   */
  private notifyTaskListeners(task: Task, reason: TaskNotificationReason): void {
    this._listeners.forEach(listener => {
      if (listener.files.find(f => task.files.includes(f))) {
        listener.callback(task, reason);
      }
    });
  }

  /**
   * @param files - the files whose tasks to subscribe to
   * @param callback - invoked for any existing tasks, updates of tasks, removal of tasks
   * @returns callback to remove the subscription
   */
  public subscribeToFiles(files: string[], callback: (task: Task) => void): () => void {
    const listener: TaskListener = {
      id: this._id++,
      files: [...files],
      callback,
      unsubscribe: () => {
        const listenerIdx = this._listeners.findIndex(l => l.id === listener.id);
        if (listenerIdx !== -1) {
          this._listeners.splice(listenerIdx, 1);
        }
      },
    };

    // Notify listener right away about existing tasks
    this._observable.tasks.forEach(task => {
      if (listener.files.find(f => task.files.includes(f))) {
        listener.callback(task, TaskNotificationReason.ADDED);
      }
    });

    this._listeners.push(listener);

    return listener.unsubscribe;
  }

  /**
   * @param files - files whose tasks to retrieve
   * @returns all the tasks of the files
   */
  public getCurrentFilesTasks(files: string[]): Task[] {
    return this._observable.tasks.filter(t => t.files.some(f => files.includes(f)));
  }
}
